// src/components/editor/EditorToolbar.jsx
import React from 'react';
import { Save, Eye, Type, Image as ImageIcon, Video, Clock } from 'lucide-react';

const EditorToolbar = ({
  currentTemplate,
  hasUnsavedChanges,
  dynamicElementsCount,
  canvasFormat,
  onCanvasFormatChange,
  onAddElement,
  onSave,
  onPreview,
  onShowDynamicManager,
  panelStates,
  onTogglePanel,
  canvasFormats,
  currentCanvasSize
}) => {
  return (
    <div className="editor-toolbar">
      <div className="px-6 py-3">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-lg font-bold text-gray-900">
                {currentTemplate?.name || 'Aucun template'}
              </h1>
              <p className="text-xs text-gray-600">
                {hasUnsavedChanges ? 'Modifications non sauvegardées' : 'Sauvegardé'}
                {dynamicElementsCount > 0 && (
                  <span className="ml-2 text-orange-600">
                    • {dynamicElementsCount} éléments variables
                  </span>
                )}
              </p>
            </div>
            
            {hasUnsavedChanges && (
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
            )}
          </div>

          <div className="flex items-center space-x-3">
            {/* Format du canvas */}
            <select
              value={canvasFormat}
              onChange={(e) => onCanvasFormatChange(e.target.value)}
              className="px-3 py-2 border rounded text-sm"
            >
              {Object.entries(canvasFormats).map(([key, format]) => (
                <option key={key} value={key}>{format.label}</option>
              ))}
            </select>

            {/* Contrôles des panneaux */}
            <div className="flex items-center space-x-1 border-r pr-3">
              {Object.entries(panelStates).map(([panel, isVisible]) => (
                <button
                  key={panel}
                  onClick={() => onTogglePanel(panel)}
                  className={`p-2 rounded transition-colors ${
                    isVisible ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  title={panel}
                >
                  {/* Icônes des panneaux selon le type */}
                </button>
              ))}
            </div>

            <button
              onClick={onPreview}
              className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Eye className="h-4 w-4" />
              <span>Prévisualiser</span>
            </button>

            <button
              onClick={onSave}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                hasUnsavedChanges
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!hasUnsavedChanges}
            >
              <Save className="h-4 w-4" />
              <span>Sauvegarder</span>
            </button>
          </div>
        </div>

        {/* Barre d'outils d'éléments */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onAddElement('text')}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            >
              <Type className="h-4 w-4" />
              <span>Texte</span>
            </button>
            
            <button
              onClick={() => onAddElement('image')}
              className="flex items-center space-x-2 px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
            >
              <ImageIcon className="h-4 w-4" />
              <span>Image</span>
            </button>
            
            <button
              onClick={() => onAddElement('video')}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
            >
              <Video className="h-4 w-4" />
              <span>Vidéo</span>
            </button>

            {dynamicElementsCount > 0 && (
              <button
                onClick={onShowDynamicManager}
                className="flex items-center space-x-2 px-3 py-2 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 ml-4 transition-colors"
              >
                <Clock className="h-4 w-4" />
                <span>{dynamicElementsCount} Variables</span>
              </button>
            )}
          </div>

          <div className="text-sm text-gray-600">
            {currentCanvasSize.width} × {currentCanvasSize.height}px
            {currentTemplate?.elements?.length > 0 && (
              <span className="ml-2">• {currentTemplate.elements.length} éléments</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorToolbar;